import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  addPayment,
  getUser,
  LocalizePropType,
  selectUser,
  withTextLocalizer,
  setBDay,
  selectDateOfBirth,
  selectIban,
  selectBIC,
  setIban,
  isKYCApp,
  setNotification,
  selectBicMandatory,
  selectAllowedSEPACountries,
  setBIC,
  selectBrandSettings,
  BrandData,
  APP,
  getAddressStatus,
  setNetworkSuccess,
  CLIENT_TYPE,
  selectTrueLayerBankAccount,
  getNextUserOpenChallengeRoute,
  isDepositRequired,
  getDepositStatus,
  isV2BankFlow,
  selectBillingInfo,
  deletePayment,
  setBillingInfo,
  isMVV,
  isIncompleteProfile,
} from 'getaway-data-layer';
import { useFormik } from 'formik';
import {
  GA_TITLES,
  isDashboardScreen,
  isStaging,
  navigateTo,
  reportGAEvent,
  showAlert,
} from '../../../../../utils';
import OptimizedSepaView from './OptimizedSepaView';
import {
  AddSepaModal,
  AddSepaSchema,
  maxBirthDate,
  minBirthDate,
} from '../../../../../validation';
import { format, isValid } from 'date-fns';
import { getTruelayerAuthLink } from 'getaway-data-layer/src/redux/store/userData/user/actions';
import { setTruelayerBankAccount } from 'getaway-data-layer/src/redux/store/ui/firstSteps/Actions';

interface Values {
  iban: string;
  bic: string;
  dateOfBirth: any;
  firstName: string;
  lastName: string;
}

type Props = {
  localize: LocalizePropType;
};

const USER_UPDATER_DURATION = 10000;

const OptimizedSepaContainer = ({ localize }: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  // @ts-ignore
  const { id } = useParams();

  const user: any = useSelector(selectUser);
  const reduxDateOfBirth: any = useSelector(selectDateOfBirth);
  const iban: any = useSelector(selectIban);
  const trueLayerBankAccount: any = useSelector(selectTrueLayerBankAccount);
  const bic: any = useSelector(selectBIC);
  const bicMandatory: any = useSelector(selectBicMandatory);
  const allowedSEPACountries: any = useSelector(selectAllowedSEPACountries);
  const addressStatus = getAddressStatus(user);
  const dateFormat = 'dd/MM/yyyy';
  const brandSettings = useSelector(selectBrandSettings) as BrandData;
  const isBusinessBillingProfile =
    isKYCApp() &&
    user?.address?.billingProfile === 'business' &&
    user?.userType !== 'org';

  const billingInfo = useSelector(selectBillingInfo);

  const existingPayment =
    isKYCApp() && user?.paymentMethods?.find((payment) => payment?.id === id);
  const truelayerIban =
    trueLayerBankAccount?.account_number?.iban || existingPayment?.sepa?.iban;

  const inCompleteProfile = isIncompleteProfile(user);
  let onboardingOnlyProduct = isKYCApp();

  const hasSyncedMerchant = user?.merchantSystemData?.find(
    (merchant) =>
      (merchant?.status === 'DONE' && merchant?.syncedAt) ||
      (merchant?.status === 'IMPORTED' && merchant?.merchantReference)
  );
  // For imported users, we consider readOnlyMode if they have been synced
  const readOnlyMode =
    (!inCompleteProfile || hasSyncedMerchant) &&
    onboardingOnlyProduct &&
    !!(isMVV() ? user?.extraData : true);
  // For imported users, synced but incomplete
  const inCompleteReadOnlyMode = readOnlyMode && inCompleteProfile;

  const disableDeletePayment =
    existingPayment &&
    (user?.blocked || inCompleteReadOnlyMode || readOnlyMode);

  const [expanded, setExpanded] = useState(false);
  const [customError, setCustomError] = useState('') as any;
  const [enableBillingProfile, setEnableBillingProfile] = useState(
    !!billingInfo
  ) as any;

  const onExpandPressed = (value: boolean) => {
    setExpanded(value);
  };

  const onSuccess = (user = null) => {
    setTimeout(() => {
      dispatch(
        getUser((updatedUser) => {
          if (
            isDashboardScreen() &&
            isDepositRequired(updatedUser) &&
            getDepositStatus(updatedUser) === 'open' &&
            updatedUser?.deposit?.toPayForSepaUnlock &&
            !updatedUser?.blocked
          ) {
            navigateTo(history, '/dashboard/deposit');
          } else {
            navigateTo(history, getNextUserOpenChallengeRoute(updatedUser));
          }
          dispatch(setNetworkSuccess(CLIENT_TYPE.USER_CLIENT.ADD_PAYMENT));
        })
      );
    }, 4000);

    reportGAEvent(GA_TITLES.ON_BOARDING.SEPA_SUBMITTED);
  };

  const onSubmit = (values: Values) => {
    if (!user?.address?.streetName) {
      dispatch(
        setNotification({
          type: APP.NOTIFICATION_TYPE.ERROR,
          message: localize('address.required'),
        })
      );
      return;
    }
    const formattedBirth = values?.dateOfBirth
      ? format(new Date(values.dateOfBirth), 'yyyy-MM-dd')
      : null;
    const sepa = {
      iban: values.iban.replace(/\s/g, ''),
      accountHolder: `${values.firstName.trim()} ${values.lastName.trim()}`,
      bic: values.bic.replace(/\s/g, ''),
    };
    dispatch(
      addPayment(
        {
          sepa,
          userInput: {
            allowCreditCheck: true,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            ...(formattedBirth && { dateOfBirth: formattedBirth.trim() }),
            ...(user?.address?.streetName && {
              address: {
                streetName: user.address?.streetName?.trim(),
                streetNumber: user.address?.streetNumber?.trim(),
                city: user.address?.city?.trim(),
                postalCode: user.address?.postalCode?.trim(),
                country: user.address?.country?.trim(),
              },
            }),
          },
          ...(user?.userType === 'org' && {
            address: { billingProfile: 'business' },
          }),
          ...(enableBillingProfile &&
            billingInfo && {
              address: { ...billingInfo, billingProfile: 'business' },
            }),
        },
        () => {
          onSuccess();
        },
        () => {},
        () => {
          setTimeout(() => navigateTo(history, '/login'), 100);
        },
        localize,
        history
      )
    );
  };

  const onClose = () => {
    if (existingPayment) {
      navigateTo(history, `/settings`);
    } else if (
      brandSettings.trueLayerSettings.bankLoginEnabled &&
      !isV2BankFlow()
    ) {
      navigateTo(
        history,
        `/${isDashboardScreen() ? 'dashboard' : 'onboarding'}/selectBank`
      );
    } else if (isDashboardScreen() || !isKYCApp()) {
      navigateTo(history, `/settings`);
    } else {
      showAlert(
        '',
        localize('cancel.FirstSteps.dialog.message'),
        localize('cancel.FirstSteps.dialog.yes'),
        () => navigateTo(history, '/settings'),
        localize('cancel.FirstSteps.dialog.no'),
        () => {},
        true
      );
    }
  };

  const onPressAddress = () => {
    if (!existingPayment) {
      navigateTo(history, '/dashboard/addAddress');
    }
  };

  const onPressAddBillingAddress = () => {
    if (!existingPayment) {
      navigateTo(history, '/dashboard/addAddress?type=business');
    }
  };

  const onPressAddressInProgress = () => {
    if (!existingPayment) {
      navigateTo(history, '/dashboard/addAddress');
    }
  };

  const onPressAddressCompleted = () => {};

  const AddSepaFormik = useFormik({
    initialValues: AddSepaModal(
      user,
      reduxDateOfBirth,
      iban ||
        truelayerIban
          ?.toUpperCase()
          ?.replace(/[^\dA-Z]/g, '')
          ?.replace(/(.{4})/g, '$1 ')
          ?.trim(),
      bic
    ),
    validationSchema: AddSepaSchema(
      localize,
      user,
      iban ||
        truelayerIban
          ?.toUpperCase()
          ?.replace(/[^\dA-Z]/g, '')
          ?.replace(/(.{4})/g, '$1 ')
          ?.trim(),
      bicMandatory,
      allowedSEPACountries
    ),
    onSubmit,
  });

  const onIbanChanged = (e: any) => {
    const valueWithSpaces = e.target.value
      .toUpperCase()
      .replace(/[^\dA-Z]/g, '')
      .replace(/(.{4})/g, '$1 ')
      .trim();
    dispatch(setIban(valueWithSpaces));
    AddSepaFormik.setFieldValue('iban', valueWithSpaces, true);
  };

  const onBicChange = (e: any) => {
    const value = e.target.value?.trim();
    dispatch(setBIC(value));
    AddSepaFormik.setFieldValue('bic', value, true);
  };

  const onDeletePayment = (id: string) => {
    showAlert(
      localize('user.deletePayment.dialog.title'),
      localize('user.deletePayment.dialog.message'),
      localize('user.deletePayment.dialog.yes'),
      () => {
        dispatch(deletePayment(id));
        dispatch(setIban(''));
        dispatch(setBillingInfo(null));
        dispatch(setTruelayerBankAccount(null));
        AddSepaFormik.setFieldValue('iban', '');
        onClose();
      },
      localize('user.deletePayment.dialog.no'),
      () => {},
      true
    );
  };

  const clearIban = () => {
    if (existingPayment) {
      onDeletePayment(id);
    } else {
      dispatch(setIban(''));
      AddSepaFormik.setFieldValue('iban', '');
    }
  };

  const deleteTruelayerIban = () => {
    if (existingPayment) {
      onDeletePayment(id);
    } else {
      dispatch(setIban(''));
      dispatch(setTruelayerBankAccount(null));
      AddSepaFormik.setFieldValue('iban', '');
    }
  };

  const openTruelayer = (providerId: string) => {
    const baseUrl = window?.location?.origin;
    const path = isDashboardScreen()
      ? '/dashboard/selectBank'
      : '/onboarding/selectBank';
    const redirectUrl = `${baseUrl}${path}`;

    const encodedRedirectUrl = encodeURIComponent(redirectUrl);

    dispatch(
      getTruelayerAuthLink(encodedRedirectUrl, (response) => {
        if (response?.link) {
          window.location.href = providerId
            ? `${response?.link}&provider_id=${providerId}`
            : response?.link;
        }
      })
    );
  };

  useEffect(() => {
    if (isValid(AddSepaFormik.values.dateOfBirth))
      dispatch(setBDay(AddSepaFormik?.values?.dateOfBirth));
  }, [AddSepaFormik.values.dateOfBirth]);

  useEffect(() => {
    let userUpdaterId;
    if (isDashboardScreen()) {
      dispatch(getUser());
    } else {
      dispatch(getUser());
      userUpdaterId = setInterval(() => {
        dispatch(getUser());
      }, USER_UPDATER_DURATION);
    }
    return () => {
      if (userUpdaterId) clearInterval(userUpdaterId);
    };
  }, []);

  return (
    <>
      <OptimizedSepaView
        localize={localize}
        user={user}
        customError={customError || AddSepaFormik.errors.dateOfBirth}
        formik={AddSepaFormik}
        truelayerIban={truelayerIban}
        deleteTruelayerIban={deleteTruelayerIban}
        onClose={onClose}
        dateFormat={dateFormat}
        minDate={minBirthDate}
        maxDate={maxBirthDate}
        expanded={expanded}
        addressStatus={addressStatus}
        address={user?.address}
        bicMandatory={bicMandatory}
        onExpandPressed={onExpandPressed}
        onIbanChanged={onIbanChanged}
        onBicChange={onBicChange}
        onPressAddress={onPressAddress}
        onPressAddressInProgress={onPressAddressInProgress}
        onPressAddressCompleted={onPressAddressCompleted}
        v2OrgSignupEnabled={brandSettings?.kycSettings?.v2OrgSignupEnabled}
        bankLoginEnabled={!!brandSettings.trueLayerSettings.bankLoginEnabled}
        openTruelayer={openTruelayer}
        clearIban={clearIban}
        isBusinessBillingProfile={isBusinessBillingProfile}
        enableBillingProfile={enableBillingProfile}
        setEnableBillingProfile={setEnableBillingProfile}
        onPressAddBillingAddress={onPressAddBillingAddress}
        billingInfo={billingInfo}
        existingPayment={existingPayment}
      />
    </>
  );
};

export default withTextLocalizer(OptimizedSepaContainer);
